import {
    Assessment,
    Celebration,
    CheckCircle,
    CloudUpload,
    NavigateBefore,
    NavigateNext,
    Psychology,
    SmartToy,
    Visibility,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Container,
    Fade,
    IconButton,
    LinearProgress,
    MobileStepper,
    Paper,
    Step,
    StepLabel,
    Stepper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import MvpAgent from '../MvpAgent';
import { useOnboardingStrings } from './data/OnboardingLangStrings';
import WelcomeStep from './steps/Step1WelcomeStep';
import CVUploadStep from './steps/Step2CVUploadStep';
import CVAnalysisResultsStep from './steps/Step3CVAnalysisResultsStep';
import ResultsOverviewStep from './steps/Step4ResultsOverviewStep';
import OverviewStep from './steps/Step5OverviewStep';
import CompletionStep from './steps/Step6CompletionStep';
import type { OnboardingData, StepConfig } from './types/OnboardingTypes';
import { CompactChatAgent } from '../CompactChatAgent';

export default function OnboardingPage() {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const strings = useOnboardingStrings('de');

    const STEPS: StepConfig[] = [
        { key: 'welcome', label: strings.welcome, icon: <Psychology /> },
        { key: 'cv-upload', label: strings.cvUpload, icon: <CloudUpload /> },
        {
            key: 'analysis',
            label: strings.analysis,
            icon: <Assessment />,
            subSteps: [strings.personalInfo, strings.skills, strings.superPowers, strings.certificates, strings.roles],
        },
        { key: 'results', label: strings.results, icon: <Visibility /> },
        { key: 'overview', label: strings.overview, icon: <CheckCircle /> },
        { key: 'completion', label: strings.completion, icon: <Celebration /> },
    ];

    const employee = useSelector((state: AppState) => state.employee);
    const companyName = useSelector((state: AppState) => state.company.name);

    const [activeStep, setActiveStep] = useState(0);
    const [analysisSubStep, setAnalysisSubStep] = useState(0);
    const [onboardingData, setOnboardingData] = useState<OnboardingData>({
        personalInfo: {
            firstName: employee?.firstName || '',
            lastName: employee?.lastName || '',
            shortDescription: employee?.description || '',
        },
        analysisResults: {
            skills: [],
            certificates: [],
            roles: [],
        },
        selectedItems: {
            skills: [],
            certificates: [],
            roles: [],
            superPowers: [],
        },
    });

    const [agentMessage, setAgentMessage] = useState('');
    const [showAgent, setShowAgent] = useState(true);
    const [chatDialogOpen, setChatDialogOpen] = useState(false);

    // Session persistence (temporary)
    useEffect(() => {
        const savedData = localStorage.getItem('onboarding-session');
        if (savedData) {
            const parsed = JSON.parse(savedData);
            setOnboardingData(parsed.data);
            setActiveStep(parsed.step);
            setAnalysisSubStep(parsed.analysisSubStep || 0);
        }
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            localStorage.setItem(
                'onboarding-session',
                JSON.stringify({
                    step: activeStep,
                    analysisSubStep: analysisSubStep,
                    data: onboardingData,
                }),
            );
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [activeStep, analysisSubStep, onboardingData]);

    const progress = ((activeStep + 1) / STEPS.length) * 100;

    const handleNext = () => {
        if (activeStep === 2) {
            // In analysis step, navigate through sub-steps
            if (analysisSubStep < 4) {
                setAnalysisSubStep(analysisSubStep + 1);
            } else {
                // Move to next main step and reset sub-step
                setActiveStep(activeStep + 1);
                setAnalysisSubStep(0);
            }
        } else if (activeStep < STEPS.length - 1) {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        if (activeStep === 2) {
            // In analysis step, navigate through sub-steps
            if (analysisSubStep > 0) {
                setAnalysisSubStep(analysisSubStep - 1);
            } else {
                // Move to previous main step
                setActiveStep(activeStep - 1);
            }
        } else if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    const updateOnboardingData = (updates: Partial<OnboardingData>) => {
        setOnboardingData((prev) => ({ ...prev, ...updates }));
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <WelcomeStep
                        firstName={onboardingData.personalInfo.firstName}
                        companyName={companyName}
                        onNext={handleNext}
                        setAgentMessage={setAgentMessage}
                    />
                );
            case 1:
                return (
                    <CVUploadStep
                        onNext={handleNext}
                        onBack={handleBack}
                        onFileUpload={(file) => updateOnboardingData({ cvFile: file })}
                        setAgentMessage={setAgentMessage}
                        data={onboardingData}
                        onDataUpdate={updateOnboardingData}
                    />
                );
            case 2:
                return (
                    <CVAnalysisResultsStep
                        onNext={handleNext}
                        onBack={handleBack}
                        setAgentMessage={setAgentMessage}
                        currentSubStep={analysisSubStep}
                        data={onboardingData}
                        onDataUpdate={(data) => {
                            const updatedData = {
                                personalInfo: data.personalInfo || onboardingData.personalInfo,
                                analysisResults: {
                                    skills: data.skills || onboardingData.analysisResults.skills,
                                    certificates: data.certificates || onboardingData.analysisResults.certificates,
                                    roles: data.roles || onboardingData.analysisResults.roles,
                                },
                                selectedItems: {
                                    skills:
                                        data.skills?.filter((s: any) => s.status === 'accepted') ||
                                        onboardingData.selectedItems.skills,
                                    certificates:
                                        data.certificates?.filter((c: any) => c.status === 'accepted') ||
                                        onboardingData.selectedItems.certificates,
                                    roles:
                                        data.roles?.filter((r: any) => r.status === 'accepted') ||
                                        onboardingData.selectedItems.roles,
                                    superPowers:
                                        data.superPowers?.filter((sp: any) => sp.status === 'accepted') ||
                                        onboardingData.selectedItems.superPowers,
                                },
                            };
                            updateOnboardingData(updatedData);
                        }}
                    />
                );
            case 3:
                return (
                    <ResultsOverviewStep
                        data={onboardingData}
                        onNext={handleNext}
                        onBack={handleBack}
                        setAgentMessage={setAgentMessage}
                        onDataUpdate={(data) => {
                            updateOnboardingData({
                                personalInfo: data.personalInfo || onboardingData.personalInfo,
                                selectedItems: data.selectedItems || onboardingData.selectedItems,
                            });
                        }}
                    />
                );
            case 4:
                return (
                    <OverviewStep
                        data={onboardingData}
                        onNext={handleNext}
                        onBack={handleBack}
                        setAgentMessage={setAgentMessage}
                    />
                );
            case 5:
                return (
                    <CompletionStep
                        data={onboardingData}
                        onComplete={() => {
                            // Keep session data, only navigate
                            setTimeout(() => {
                                navigate('/dashboard');
                            }, 2000);
                        }}
                        setAgentMessage={setAgentMessage}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4, minHeight: '100vh', px: 1 }}>
            {/* Futuristic Header */}
            <Paper
                elevation={0}
                sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    borderRadius: 3,
                    p: 3,
                    mb: 4,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            width: 48,
                            height: 48,
                        }}
                    >
                        <Psychology />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Dein Onboarding {onboardingData.personalInfo.firstName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Dein persönlicher Onboarding-Prozess bei {companyName}
                        </Typography>
                    </Box>
                </Box>

                {/* Progress Bar with Navigation */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {strings.progress}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {Math.round(progress)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            },
                        }}
                    />
                </Box>

                {/* Navigation Stepper */}
                <MobileStepper
                    variant="text"
                    steps={STEPS.length}
                    position="static"
                    activeStep={activeStep}
                    sx={{
                        backgroundColor: 'transparent',
                        p: 0,
                        '& .MuiMobileStepper-progress': {
                            width: '100%',
                        },
                    }}
                    nextButton={
                        <Button
                            size="small"
                            onClick={handleNext}
                            disabled={activeStep === STEPS.length - 1}
                            endIcon={<NavigateNext />}
                            sx={{ textTransform: 'none' }}
                        >
                            {strings.next}
                        </Button>
                    }
                    backButton={
                        <Button
                            size="small"
                            onClick={handleBack}
                            disabled={activeStep === 0}
                            startIcon={<NavigateBefore />}
                            sx={{ textTransform: 'none' }}
                        >
                            {strings.back}
                        </Button>
                    }
                />
            </Paper>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 2 : 3,
                    minHeight: '60vh',
                }}
            >
                {/* Desktop Stepper */}
                {!isMobile && (
                    <Paper
                        elevation={0}
                        role="navigation"
                        aria-label="Onboarding steps"
                        sx={{
                            width: 280,
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            background: alpha(theme.palette.background.paper, 0.7),
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {STEPS.map((step, index) => (
                                <React.Fragment key={step.key}>
                                    <Step>
                                        <StepLabel
                                            icon={
                                                <Avatar
                                                    role="button"
                                                    tabIndex={index < activeStep ? 0 : -1}
                                                    aria-label={`${strings.step} ${index + 1}: ${step.label}${index < activeStep ? ' (clickable)' : ''}`}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        bgcolor:
                                                            index <= activeStep
                                                                ? theme.palette.primary.main
                                                                : 'transparent',
                                                        border: `2px solid ${index <= activeStep ? theme.palette.primary.main : theme.palette.divider}`,
                                                        color:
                                                            index <= activeStep
                                                                ? 'white'
                                                                : theme.palette.text.secondary,
                                                        cursor: index < activeStep ? 'pointer' : 'default',
                                                    }}
                                                    onClick={() => {
                                                        if (index < activeStep) {
                                                            setActiveStep(index);
                                                            setAnalysisSubStep(0);
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (
                                                            (e.key === 'Enter' || e.key === ' ') &&
                                                            index < activeStep
                                                        ) {
                                                            setActiveStep(index);
                                                            setAnalysisSubStep(0);
                                                        }
                                                    }}
                                                >
                                                    {index < activeStep ? (
                                                        <CheckCircle sx={{ fontSize: 18 }} />
                                                    ) : (
                                                        React.cloneElement(step.icon, { sx: { fontSize: 18 } })
                                                    )}
                                                </Avatar>
                                            }
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: index === activeStep ? 600 : 400,
                                                    color:
                                                        index === activeStep
                                                            ? theme.palette.primary.main
                                                            : index < activeStep
                                                              ? theme.palette.text.primary
                                                              : theme.palette.text.secondary,
                                                    cursor: index < activeStep ? 'pointer' : 'default',
                                                }}
                                                onClick={() => {
                                                    if (index < activeStep) {
                                                        setActiveStep(index);
                                                        setAnalysisSubStep(0);
                                                    }
                                                }}
                                            >
                                                {step.label}
                                            </Typography>
                                        </StepLabel>
                                    </Step>

                                    {/* Sub-steps for Analysis */}
                                    {step.subSteps && (index === activeStep || (index === 2 && activeStep > 2)) && (
                                        <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                                            {step.subSteps.map((subStep, subIndex) => {
                                                const isClickable =
                                                    index === activeStep || (index === 2 && activeStep > 2);
                                                const isCompleted = index === 2 && activeStep > 2;
                                                return (
                                                    <Box
                                                        key={subIndex}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            py: 0.5,
                                                            cursor: isClickable ? 'pointer' : 'default',
                                                        }}
                                                        onClick={() => {
                                                            if (isClickable && index === 2) {
                                                                setActiveStep(2);
                                                                setAnalysisSubStep(subIndex);
                                                            }
                                                        }}
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                bgcolor:
                                                                    (subIndex === analysisSubStep &&
                                                                        index === activeStep) ||
                                                                    isCompleted
                                                                        ? theme.palette.primary.main
                                                                        : 'transparent',
                                                                border: `1px solid ${(subIndex === analysisSubStep && index === activeStep) || isCompleted ? theme.palette.primary.main : theme.palette.divider}`,
                                                                fontSize: '0.75rem',
                                                                color:
                                                                    (subIndex === analysisSubStep &&
                                                                        index === activeStep) ||
                                                                    isCompleted
                                                                        ? 'white'
                                                                        : theme.palette.text.secondary,
                                                            }}
                                                        >
                                                            {isCompleted ? (
                                                                <CheckCircle sx={{ fontSize: 12 }} />
                                                            ) : (
                                                                subIndex + 1
                                                            )}
                                                        </Avatar>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: '0.9rem',
                                                                fontWeight:
                                                                    (subIndex === analysisSubStep &&
                                                                        index === activeStep) ||
                                                                    isCompleted
                                                                        ? 600
                                                                        : 400,
                                                                color:
                                                                    (subIndex === analysisSubStep &&
                                                                        index === activeStep) ||
                                                                    isCompleted
                                                                        ? theme.palette.primary.main
                                                                        : theme.palette.text.secondary,
                                                            }}
                                                        >
                                                            {subStep}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </React.Fragment>
                            ))}
                        </Stepper>
                    </Paper>
                )}

                {/* Main Content */}
                <Box sx={{ flex: 1 }}>
                    {/* Agent Section */}
                    {showAgent && (
                        <Paper
                            elevation={0}
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                                backdropFilter: 'blur(10px)',
                                overflow: 'visible',
                                position: 'relative',
                                zIndex: 1,
                            }}
                        >
                            <MvpAgent
                                message={agentMessage}
                                context="onboarding"
                                step={activeStep}
                                onClose={() => setShowAgent(false)}
                                onOpenMainChat={() => setChatDialogOpen(true)}
                                actions={
                                    activeStep === 2
                                        ? [
                                              {
                                                  label: 'Level erklären',
                                                  onClick: () => {
                                                      // Level explanation logic
                                                  },
                                                  variant: 'outlined',
                                              },
                                              {
                                                  label: 'CV neu analysieren',
                                                  onClick: () => {
                                                      setActiveStep(1);
                                                      setAnalysisSubStep(0);
                                                  },
                                                  variant: 'outlined',
                                              },
                                          ]
                                        : []
                                }
                            />
                        </Paper>
                    )}

                    <Paper
                        elevation={0}
                        sx={{
                            p: isMobile ? 2 : 3,
                            m: 0,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            background: alpha(theme.palette.background.paper, 0.9),
                            backdropFilter: 'blur(10px)',
                            minHeight: '60vh',
                        }}
                    >
                        <Fade in key={activeStep}>
                            <Box>{renderStepContent()}</Box>
                        </Fade>
                    </Paper>
                </Box>
            </Box>

            {/* Mobile Stepper - Fixed Bottom */}
            {isMobile && (
                <MobileStepper
                    variant={isSmallMobile ? 'dots' : 'progress'}
                    steps={STEPS.length}
                    position="bottom"
                    activeStep={activeStep}
                    role="navigation"
                    aria-label="Mobile onboarding navigation"
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        zIndex: 1000,
                        px: isSmallMobile ? 1 : 2,
                    }}
                    nextButton={
                        <IconButton
                            size={isSmallMobile ? 'small' : 'medium'}
                            onClick={handleNext}
                            disabled={activeStep === STEPS.length - 1}
                            aria-label={strings.next}
                        >
                            <NavigateNext />
                        </IconButton>
                    }
                    backButton={
                        <IconButton
                            size={isSmallMobile ? 'small' : 'medium'}
                            onClick={handleBack}
                            disabled={activeStep === 0}
                            aria-label={strings.back}
                        >
                            <NavigateBefore />
                        </IconButton>
                    }
                />
            )}

            {/* Help Agent Button */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: isMobile ? 80 : 24,
                    right: 24,
                    zIndex: 1000,
                }}
            >
                <IconButton
                    onClick={() => setShowAgent(!showAgent)}
                    aria-label={showAgent ? 'Hide AI assistant' : 'Show AI assistant'}
                    sx={{
                        width: isSmallMobile ? 48 : 56,
                        height: isSmallMobile ? 48 : 56,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                        color: 'white',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    <SmartToy />
                </IconButton>
            </Box>
            
            {/* CompactChatAgent Dialog */}
            <CompactChatAgent 
                open={chatDialogOpen} 
                onClose={() => setChatDialogOpen(false)} 
            />
        </Container>
    );
}
